/**
 * DOS518 - Digital Operations System for Wildfire Management
 * Copyright (c) 2024-2025 Angelo Irtilaç — All rights reserved.
 *
 * GPSModal.tsx — Cattura rapida GPS per ricognizione AIB.
 * Flusso a 2 tap: apri → selezione tipo → salvato.
 * Auto-naming progressivo: Posizione DOS, P1, P2, P3...
 */
// src/components/Coordinate/GPSModal.tsx
import React, { useState, useEffect } from 'react';
import { useAppStore } from '../../store/useAppStore';
import './GPSModal.css';

interface GPSModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type CoordTipo = 'fronte' | 'vedetta' | 'risorsa_idrica' | 'base' | 'altro';
type Phase = 'acquiring' | 'ready' | 'error';

const TIPI: { tipo: CoordTipo; emoji: string; label: string; color: string; bg: string }[] = [
  { tipo: 'fronte',        emoji: '🔥', label: 'FRONTE',  color: '#ef4444', bg: 'rgba(239,68,68,0.15)' },
  { tipo: 'vedetta',       emoji: '👁',  label: 'VEDETTA', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  { tipo: 'risorsa_idrica',emoji: '💧', label: 'ACQUA',   color: '#3b82f6', bg: 'rgba(59,130,246,0.15)' },
  { tipo: 'base',          emoji: '🏠', label: 'BASE',    color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
  { tipo: 'altro',         emoji: '📍', label: 'ALTRO',   color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' },
];

export const GPSModal: React.FC<GPSModalProps> = ({ isOpen, onClose }) => {
  const [phase, setPhase]       = useState<Phase>('acquiring');
  const [gpsData, setGpsData]   = useState<{ lat: number; lon: number; accuracy: number; altitude: number | null } | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [salvato, setSalvato]   = useState(false);

  const coordinate  = useAppStore(state => state.coordinate);
  const addCoordinate = useAppStore(state => state.addCoordinate);

  const isPrimaCoordinata = coordinate.length === 0;

  // Auto-avvio GPS all'apertura
  useEffect(() => {
    if (!isOpen) return;
    setPhase('acquiring');
    setGpsData(null);
    setErrorMsg('');
    setSalvato(false);
    acquireGPS();
  }, [isOpen]);

  const acquireGPS = () => {
    setPhase('acquiring');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsData({
          lat:      pos.coords.latitude,
          lon:      pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          altitude: pos.coords.altitude,
        });
        setPhase('ready');
      },
      (err) => {
        const msgs: Record<number, string> = {
          1: 'Permessi GPS negati. Abilita la geolocalizzazione.',
          2: 'Segnale GPS non disponibile. Muoviti all\'aperto.',
          3: 'Timeout. Riprova in campo aperto.',
        };
        setErrorMsg(msgs[err.code] ?? err.message);
        setPhase('error');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  // Salvataggio al click sul tipo — 2° tap
  const handleSalva = (tipo: CoordTipo) => {
    if (!gpsData) return;

    const nome = isPrimaCoordinata ? 'Posizione DOS' : `P${coordinate.length}`;
    const ora  = new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    addCoordinate({
      id:                    Date.now() as any,
      lat:                   gpsData.lat.toFixed(6),
      lon:                   gpsData.lon.toFixed(6),
      active:                true,
      titolo:                nome,
      note:                  '',
      tipo,
      capoSquadra:           '',
      timestamp:             ora,
      timestampAssegnazione: '',
      accuracy:              gpsData.accuracy,
      altitude:              gpsData.altitude ?? undefined,
    } as any);

    setSalvato(true);
    setTimeout(() => { onClose(); }, 700);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content gps-modal"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 420 }}
      >
        {/* HEADER */}
        <div className="modal-header" style={{ background: '#ff6b35' }}>
          <h3>📡 ACQUISIZIONE GPS</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">

          {/* ── FASE: ACQUIRING ── */}
          {phase === 'acquiring' && (
            <div style={S.acquiring}>
              <div style={S.spinner} />
              <p style={S.acquiringText}>Acquisizione segnale GPS…</p>
              <p style={S.acquiringSub}>Assicurati di essere all'aperto</p>
            </div>
          )}

          {/* ── FASE: ERROR ── */}
          {phase === 'error' && (
            <div style={S.errorBox}>
              <p style={{ color: '#ef4444', fontWeight: 700, margin: '0 0 12px' }}>❌ {errorMsg}</p>
              <button style={S.btnRetry} onClick={acquireGPS}>🔄 Riprova</button>
            </div>
          )}

          {/* ── FASE: READY ── */}
          {phase === 'ready' && gpsData && !salvato && (
            <>
              {/* Coordinate */}
              <div style={S.coordRow}>
                <div style={S.coordBox}>
                  <span style={S.coordLabel}>LAT</span>
                  <span style={S.coordValue}>{gpsData.lat.toFixed(6)}</span>
                </div>
                <div style={S.coordBox}>
                  <span style={S.coordLabel}>LON</span>
                  <span style={S.coordValue}>{gpsData.lon.toFixed(6)}</span>
                </div>
              </div>

              {/* Accuratezza */}
              <div style={{
                ...S.accuracyBadge,
                background: gpsData.accuracy < 20 ? 'rgba(34,197,94,0.1)' : gpsData.accuracy < 50 ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)',
                borderColor: gpsData.accuracy < 20 ? '#22c55e' : gpsData.accuracy < 50 ? '#f59e0b' : '#ef4444',
                color:       gpsData.accuracy < 20 ? '#22c55e' : gpsData.accuracy < 50 ? '#f59e0b' : '#ef4444',
              }}>
                {gpsData.accuracy < 20 ? '✅' : gpsData.accuracy < 50 ? '⚠️' : '❌'}
                {' '}Precisione: ±{Math.round(gpsData.accuracy)}m
                {gpsData.accuracy >= 50 && (
                  <span style={{ display: 'block', fontSize: 11, marginTop: 2 }}>
                    Segnale scarso — muoviti all'aperto o riprova
                  </span>
                )}
              </div>

              {/* Prima coordinata */}
              {isPrimaCoordinata && (
                <div style={S.dosNotice}>
                  📍 Questa sarà salvata come <strong>Posizione DOS</strong>
                  <button
                    style={S.btnTipoLarge}
                    onClick={() => handleSalva('altro')}
                  >
                    💾 Salva Posizione DOS
                  </button>
                </div>
              )}

              {/* Selezione tipo — 4 grandi pulsanti */}
              {!isPrimaCoordinata && (
                <>
                  <p style={S.tipoHint}>
                    Seleziona il tipo — punto salvato come <strong>P{coordinate.length}</strong>
                  </p>
                  <div style={S.tipiGrid}>
                    {TIPI.map(({ tipo, emoji, label, color, bg }) => (
                      <button
                        key={tipo}
                        style={{ ...S.btnTipo, background: bg, borderColor: color, color }}
                        onClick={() => handleSalva(tipo)}
                      >
                        <span style={{ fontSize: 28 }}>{emoji}</span>
                        <span style={{ fontWeight: 800, fontSize: 15, letterSpacing: 0.5 }}>{label}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}

            </>
          )}

          {/* ── SALVATO ── */}
          {salvato && (
            <div style={S.salvatoBox}>
              <span style={{ fontSize: 40 }}>✅</span>
              <p style={{ color: '#22c55e', fontWeight: 700, margin: 0 }}>Punto salvato!</p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// ─── Stili ────────────────────────────────────────────────────────────────────

const S: Record<string, React.CSSProperties> = {
  acquiring: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '30px 0', gap: 12,
  },
  spinner: {
    width: 40, height: 40,
    border: '4px solid rgba(255,107,53,0.2)',
    borderTop: '4px solid #ff6b35',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  acquiringText: { color: '#fff', fontWeight: 700, margin: 0, fontSize: 16 },
  acquiringSub:  { color: '#94a3b8', margin: 0, fontSize: 13 },
  errorBox: {
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid #ef4444',
    borderRadius: 8, padding: 16, marginBottom: 8,
  },
  btnRetry: {
    padding: '10px 20px', borderRadius: 7, border: 'none',
    background: '#ef4444', color: '#fff', fontWeight: 700,
    cursor: 'pointer', fontSize: 14,
  },
  coordRow: { display: 'flex', gap: 10, marginBottom: 10 },
  coordBox: {
    flex: 1, padding: '10px 12px',
    background: 'rgba(255,107,53,0.08)',
    border: '1px solid rgba(255,107,53,0.3)',
    borderRadius: 8,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
  },
  coordLabel: { color: '#ff6b35', fontSize: 10, fontWeight: 800, letterSpacing: 1 },
  coordValue: { color: '#fff', fontFamily: 'monospace', fontWeight: 700, fontSize: 15 },
  accuracyBadge: {
    padding: '8px 12px', borderRadius: 7,
    border: '1px solid', fontSize: 13,
    fontWeight: 600, marginBottom: 14,
  },
  dosNotice: {
    background: 'rgba(52,152,219,0.08)',
    border: '2px solid #3498db',
    borderRadius: 8, padding: 14,
    color: '#60a5fa', fontSize: 14,
    display: 'flex', flexDirection: 'column', gap: 12,
  },
  tipoHint: {
    color: '#94a3b8', fontSize: 12, textAlign: 'center',
    margin: '0 0 10px',
  },
  tipiGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
  },
  btnTipo: {
    padding: '18px 10px',
    borderRadius: 10,
    border: '2px solid',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: 6,
    cursor: 'pointer',
    transition: 'transform 0.1s',
    fontWeight: 700,
  },
  btnTipoLarge: {
    padding: '14px', borderRadius: 8, border: 'none',
    background: '#3498db', color: '#fff',
    fontWeight: 700, fontSize: 15, cursor: 'pointer',
    width: '100%',
  },
  salvatoBox: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', padding: '30px 0', gap: 10,
  },
};
